/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useLocale } from "@superweb/intl";
import { brandIcons, proServiceIcons, serviceIcons } from "@superweb/ui";

type RoughSpecification = "taxi" | "eats" | "smena" | "delivery";

const brandSpecificationIcons = {
  taxi: serviceIcons.Taxi,
  taxiYango: brandIcons.YandexEN,
  delivery: serviceIcons.Delivery,
  deliveryYango: serviceIcons.Deli,
  eats: serviceIcons.Eats,
  smena: proServiceIcons.Smena,
};

const specificationOrder = {
  taxi: 1,
  delivery: 2,
  eats: 3,
  smena: 4,
};

const getRoughSpecification = (
  specification: string,
): RoughSpecification | undefined => {
  switch (specification) {
    case "taxi":
    case "smena":
    case "delivery":
      return specification;
    case "eda":
      return "eats";
    default:
      return undefined;
  }
};

const SpecificationIcon = ({
  specification,
  brand,
  size,
}: {
  specification: RoughSpecification;
  brand: string;
  size?: "s" | "m";
}) => {
  const width = size === "m" ? 20 : 14;
  const height = size === "m" ? 20 : 14;

  switch (specification) {
    case "taxi":
      return brand === "yango" ? (
        <brandSpecificationIcons.taxiYango
          aria-label={specification}
          width={width}
          height={height}
        />
      ) : (
        <brandSpecificationIcons.taxi
          aria-label={specification}
          width={width}
          height={height}
        />
      );
    case "delivery":
      return brand === "yango" ? (
        <brandSpecificationIcons.deliveryYango
          aria-label={specification}
          width={width}
          height={height}
        />
      ) : (
        <brandSpecificationIcons.delivery
          aria-label={specification}
          width={width}
          height={height}
        />
      );
    case "eats":
      return (
        <brandSpecificationIcons.eats
          aria-label={specification}
          width={width}
          height={height}
        />
      );
    case "smena":
      return (
        <brandSpecificationIcons.smena
          aria-label={specification}
          width={width}
          height={height}
        />
      );
    default:
      return null;
  }
};

export const Specifications = ({
  specifications,
  brand,
  size = "m",
}: {
  specifications: string[];
  brand: string;
  size?: "s" | "m";
}) => {
  const {
    textInfo: { direction },
  } = useLocale();

  return (
    <ul
      css={{
        display: "flex",
        width: "min-content",
        marginInlineStart: "4px",
        marginBlockStart: "4px",
        direction: direction === "ltr" ? "rtl" : "ltr",
      }}
    >
      {specifications
        .map(getRoughSpecification)
        .flatMap((spec) => spec || [])
        .sort((a, b) => specificationOrder[b] - specificationOrder[a])
        .map((name) => (
          <li key={name} css={{ marginInlineStart: "-4px" }}>
            <SpecificationIcon specification={name} brand={brand} size={size} />
          </li>
        ))}
    </ul>
  );
};
